import type { EndUser } from "@loot-os/client";
import type { AccountVerificationContent } from "@loot-os/design-system/templates/account/accountContent";

import { type KycUiState, kycUiState } from "../../../../lib/kyc.js";
import { useStartKyc } from "../../../../lib/shopper.js";
import { useTerminology } from "../../../../lib/terminology.js";

const STATUS: Record<KycUiState, AccountVerificationContent["status"]> = {
  canStart: { tone: "neutral", badge: "Not verified", text: "Not verified yet." },
  processing: {
    tone: "warning",
    badge: "Pending",
    text: "Identity verification is pending. This page updates as soon as a decision arrives.",
  },
  approved: { tone: "success", badge: "Verified", text: "Your identity is verified." },
  declined: {
    tone: "danger",
    badge: "Denied",
    text: "Identity verification was denied. Contact support for additional information.",
  },
};

const ACTION_LABEL: Record<KycUiState, string> = {
  canStart: "Verify identity",
  processing: "Pending",
  approved: "Identity verified",
  declined: "Denied",
};

/**
 * Settings → Verification, in a store with `kycRequired`: Veriff's statuses folded into four
 * states (`kycUiState`), and POST /v1/me/kyc/session to start, which opens in a new tab.
 */
export function useVerificationContent({
  me,
  locked,
  setError,
}: {
  me: EndUser | null;
  locked: boolean;
  setError: (message: string | null) => void;
}): AccountVerificationContent {
  const { item } = useTerminology();
  const startKyc = useStartKyc();
  const state = kycUiState(me);
  return {
    title: "Identity verification",
    description: `This store verifies your identity before ${item.plural} can be shipped to you.`,
    status: STATUS[state],
    notes:
      state === "canStart"
        ? [
            "Verify with a physical ID document. A photo of an ID, or an ID shown on a screen, is rejected and can lock your account.",
            "Verification opens in a new tab. If nothing opens, allow pop-ups for this site.",
          ]
        : [],
    action: {
      label: state === "canStart" && startKyc.isPending ? "Opening…" : ACTION_LABEL[state],
      emphasis: state === "canStart" ? "primary" : "secondary",
      disabled: state !== "canStart" || locked || startKyc.isPending,
      onClick: () => {
        setError(null);
        startKyc.mutate(undefined, {
          onError: (err) =>
            setError(
              err instanceof Error && err.message
                ? `Could not start identity verification: ${err.message}`
                : "Could not start identity verification. Try again in a moment.",
            ),
        });
      },
    },
    provider: "Powered by Veriff",
  };
}
